import { useContext } from 'react';
import {
  ButtonIcon,
  CardWrapperWithHeader,
  Table,
  formatUnixTime,
  getCurrencyFormat,
} from '@rabbit/elements/shared-components';
import { calculateTotalValue, formatAddress } from '../../../../utils/helpers';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/solid';
import {
  liteAdministrativeTimeColumns,
  liteOtherColumns,
  litePartsUsedColumns,
  litePostageColumns,
  liteRepairTimeColumns,
  liteTravelColumns,
  type1PartsUsedColumns,
  type1PostageColumns,
} from './ClaimCostsSectionTableColumns';
import { LIST_CURRENCIES } from '@rabbit/bizproc/react';
import { UserUploadedDocument } from '@rabbit/data/types';
import { useTranslation } from 'react-i18next';
import {
  CaseFactsShape,
  CFCF_AdministrativeCostLog,
  CFCF_OtherCostLog,
  CFCF_TravelCostLog,
  ClaimPartUsed,
} from '@rabbit/bizproc/core';
import { AppContext } from '@rabbit/app-context';
import {
  DocumentModalShape,
  LogClaimEditShape,
  ClaimCostsModalDataShape,
} from './ClaimCostsSection';

export interface ClaimCostsSectionDefaultProps {
  caseFacts: Partial<CaseFactsShape>;
  setLogCostModal: (value: boolean) => void;
  setEditModal: (value: LogClaimEditShape | undefined) => void;
  setDocumentModal: (value: DocumentModalShape | undefined) => void;
  // For enabling Type1-style edit/delete actions on Type1 tables inside Default
  setModalData?: (data: ClaimCostsModalDataShape) => void;
  /** Archived claims still show their cost history, just without write controls. */
  readOnly?: boolean;
}

export function ClaimCostsSectionDefault({
  caseFacts,
  setDocumentModal,
  setEditModal,
  setLogCostModal,
  setModalData,
  readOnly = false,
}: ClaimCostsSectionDefaultProps) {
  const { tenantInfo } = useContext(AppContext);
  const { config } = useContext(AppContext);
  const { t } = useTranslation();

  /* -------------------------------------------------------------------------- */
  /*                             Results formatting                             */
  /* -------------------------------------------------------------------------- */

  /* ------------------------------- Parts used ------------------------------- */
  const getPartsUserArrayParsed = () => {
    const partsUsedArray: ClaimPartUsed[] =
      caseFacts?.parts_used_cost_data ?? [];

    const partsUserArrayParsed: {
      data: object;
      manufacturer: string;
      description: string;
      part_name: string;
      model_id: string;
      parts_quantity: string;
      parts_cost: string;
      parts_cost_vat: string;
      subtotal: string;
    }[] = [];
    partsUsedArray.forEach((result, index) => {
      partsUserArrayParsed.push({
        data: { ...result, index },
        description: result?.description ?? '-',
        part_name: result?.name,
        manufacturer: result?.manufacturer as any,
        model_id: result?.id,
        parts_quantity: String(result?.quantity),
        parts_cost: result?.cost
          ? getCurrencyFormat(result?.cost?.amount, result?.cost?.currency)
          : '0',
        parts_cost_vat: result?.VAT ? result?.VAT + '%' : '0',
        subtotal:
          getCurrencyFormat(
            Number(
              parseInt((result?.quantity as any).replace(/[^\d.-]/g, ''))
            ) * Number(result?.cost?.amount),
            result?.cost?.currency
          ) ?? '0',
      });
    });
    return partsUserArrayParsed;
  };

  const partsUserArrayParsed: any[] = getPartsUserArrayParsed();
  const partsUsedTotalCost = partsUserArrayParsed.reduce((acc, curr) => {
    const subTotal =
      curr.subtotal && parseFloat(curr.subtotal.replace(/[^\d.-]/g, ''))
        ? parseFloat(curr.subtotal.replace(/[^\d.-]/g, ''))
        : 0;
    return (
      acc +
      //@ts-ignore todo: review this
      subTotal +
      (subTotal * curr.data.VAT) / 100
    );
  }, 0);

  /* ------------------------ Type1 Parts used (additional) ------------------------ */
  // Pull parts used from repair_work_registry like ClaimCostsSectionType1
  const getType1PartsUserArrayParsed = () => {
    const partsUsedArray: any[] = [];
    caseFacts?.repair_work_registry?.forEach((item: any) => {
      if (item?.parts_used_for_repair) {
        partsUsedArray.push(...item.parts_used_for_repair);
      }
    });

    const partsUserArrayParsedType1: {
      data: object;
      description: string;
      repairer_id: string;
      part_name: string;
      model_id: string;
      parts_quantity: string;
      parts_cost: string;
      subtotal: string;
    }[] = [];
    partsUsedArray.forEach((result) => {
      if (result?.part_name)
        partsUserArrayParsedType1.push({
          data: result,
          description: result?.description ?? '-',
          repairer_id: '-',
          part_name: result?.part_name,
          model_id: result?.model_id,
          parts_quantity: String(result?.parts_quantity ?? ''),
          parts_cost: result?.parts_cost
            ? getCurrencyFormat(
                result?.parts_cost?.amount,
                result?.parts_cost?.currency
              )
            : '0',
          subtotal:
            getCurrencyFormat(
              Number(result?.parts_quantity ?? 0) *
                Number(result?.parts_cost?.amount ?? 0),
              result?.parts_cost?.currency ?? tenantInfo?.currency ?? 'GBP'
            ) ?? '0',
        });
    });
    return partsUserArrayParsedType1;
  };

  const type1PartsUserArrayParsed: any[] = getType1PartsUserArrayParsed();
  const type1PartsUsedTotalCost = type1PartsUserArrayParsed.reduce(
    (acc, curr) => {
      const sub = curr.subtotal
        ? parseFloat(String(curr.subtotal).replace(/[^\d.-]/g, ''))
        : 0;
      return acc + sub;
    },
    0
  );

  /* --------------------------- Administrative time -------------------------- */
  // TODO: CHECK THE RATE. WAS SET TO AN ENV VAR BASED ON SHELTA PREVIOUSLY
  const RATE = 15;
  const getFormattedResultAdministrativeTime = () => {
    const formattedResultAdministrativeTime: {
      data: object;
      description: string;
      repairer_id: string;
      total_repairing_time: string;
      timeStamp: string;
      cost: string;
      vat: string;
    }[] = [];
    caseFacts.administrative_cost_data?.forEach(
      (result: CFCF_AdministrativeCostLog, i) => {
        formattedResultAdministrativeTime.push({
          data: { ...result, index: i },
          description: result?.description || '-',
          repairer_id: result?.team_member.name || '-',
          total_repairing_time: result?.time_spent || '-',
          timeStamp: result?.date
            ? formatUnixTime(Number(result?.date), 'dd/MM/yyyy')
            : '-',
          cost: result?.time_spent
            ? getCurrencyFormat(
                result?.rate.amount || 0,
                tenantInfo?.currency || 'GBP'
              )
            : '-',
          vat: result?.VAT ? result?.VAT + '%' : '-',
        });
      }
    );
    return formattedResultAdministrativeTime;
  };

  const formattedResultAdministrativeTime =
    getFormattedResultAdministrativeTime();

  /* ----------------------------- Assessment time ---------------------------- */
  const assessmentTime = [
    {
      repairer: caseFacts?.delegate_repairer_name,
      time_spent_assessing: caseFacts?.time_spent_assessing,
      cost: caseFacts?.time_spent_assessing,
      timeStamp: Date.now(),
    },
  ];
  const formattedResultAssessmentTime: {
    data: object;
    repairer_id: string;
    total_repairing_time: string;
    timeStamp: string;
    cost: string;
  }[] = [];
  assessmentTime.forEach((result) => {
    if (result?.time_spent_assessing) {
      if (typeof result?.time_spent_assessing === 'string') {
        formattedResultAssessmentTime.push({
          data: result,
          repairer_id: result?.repairer || '-',
          total_repairing_time: result?.time_spent_assessing || '-',
          timeStamp: result?.timeStamp
            ? formatUnixTime(result?.timeStamp, 'dd/MM/yyyy')
            : '-',
          cost: result?.time_spent_assessing
            ? getCurrencyFormat(
                calculateTotalValue(result?.time_spent_assessing, RATE),
                tenantInfo?.currency || ''
              )
            : '-',
        });
      } else {
        result?.time_spent_assessing.forEach((timeSpent) => {
          formattedResultAssessmentTime.push({
            data: result,
            repairer_id: timeSpent.user || '-',
            total_repairing_time: timeSpent.time || '-',
            timeStamp: result?.timeStamp
              ? formatUnixTime(result?.timeStamp, 'dd/MM/yyyy')
              : '-',
            cost: timeSpent.time
              ? getCurrencyFormat(
                  calculateTotalValue(timeSpent.time, RATE),
                  tenantInfo?.currency || 'GBP'
                )
              : '-',
          });
        });
      }
    }
  });

  const finalArrayForAdministrativeTimeTable: any[] =
    formattedResultAssessmentTime.concat(formattedResultAdministrativeTime);

  const administratitveTotalCost = finalArrayForAdministrativeTimeTable.reduce(
    (acc, cur) => {
      return (
        acc +
        calculateTotalValue(
          // time string like "hh:mm" or similar; default to '0:00' if missing
          (cur?.data as any)?.time_spent || '0:00',
          // hourly rate numeric; default to 0 if missing
          (cur?.data as any)?.rate?.amount ?? 0,
          // VAT numeric; default to 0 if missing
          (cur?.data as any)?.VAT ?? 0
        )
      );
    },
    0
  );

  /* ------------------------------- Repair time ------------------------------ */
  const getFormattedResultRepairTime = () => {
    const repairTime = caseFacts?.repair_time_data || [];
    const formattedResultRepairTime: {
      data: object;
      description: string;
      repairer_id: string;
      total_repairing_time: string;
      timeStamp: string;
      cost: string;
      vat: string;
    }[] = [];
    repairTime.forEach((result, i) => {
      const formattedResult = {
        data: { ...result, index: i },
        description: result?.description || '-',
        repairer_id: result?.team_member.name || '-',
        total_repairing_time: result?.time_spent || '-',
        timeStamp:
          result?.date && result?.date !== 0
            ? formatUnixTime(Number(result?.date), 'dd/MM/yyyy')
            : '-',
        cost: result?.rate?.amount
          ? getCurrencyFormat(result.rate.amount ?? 0, result?.rate.currency)
          : '-',
        vat: result?.VAT ? result?.VAT + '%' : '-',
      };

      formattedResultRepairTime.push(formattedResult);
    });
    return formattedResultRepairTime;
  };

  // todo type
  const formattedResultRepairTime: any[] = getFormattedResultRepairTime();

  const repairTotalCost =
    caseFacts?.repair_time_data?.reduce((acc, curr) => {
      return (
        acc +
        calculateTotalValue(
          curr.time_spent,
          curr.rate?.amount ? curr.rate.amount : 0,
          curr.VAT
        )
      );
    }, 0) || 0;

  /* --------------------------------- Postage -------------------------------- */
  const getFormattedResultPostage = () => {
    const postage = caseFacts?.shipping_cost_data || [];
    const formattedResultPostage: {
      data: object;
      description: string;
      carrier: string;
      tracking_number: string;
      date: string;
      cost: string;
      vat: string;
      attachments: UserUploadedDocument[];
    }[] = [];
    postage.forEach((result, i) => {
      formattedResultPostage.push({
        data: { ...result, index: i },
        description: result?.description || '-',
        carrier: (result?.carrier as any) || '-',
        tracking_number: result?.tracking_number || '-',
        date: result?.date
          ? formatUnixTime(Number(result?.date), 'dd/MM/yyyy')
          : '-',
        cost: result?.amount
          ? getCurrencyFormat(result?.amount?.amount, result?.amount?.currency)
          : '-',
        vat: String(result?.VAT || '-'),
        attachments: result?.documents || [],
      });
    });
    return formattedResultPostage;
  };

  const formattedResultPostage: any[] = getFormattedResultPostage();
  const postageTotalCost = formattedResultPostage.reduce((acc, curr) => {
    const subTotal = curr.cost
      ? parseFloat(curr.cost.replace(/[^\d.-]/g, ''))
      : 0;
    return acc + subTotal + (subTotal * curr.data.VAT) / 100;
  }, 0);

  /* ------------------- Type1 Postage (additional separate table) ------------------- */
  const getType1FormattedPostage = () => {
    const postage = (caseFacts as any)?.postage_registry || [];
    const formattedResultPostageType1: {
      carrier: string;
      tracking_number: string;
      address: string | JSX.Element;
      date: string;
      cost: string;
    }[] = [];
    postage.forEach((result: any) => {
      if (result?.outbound_carrier) {
        formattedResultPostageType1.push({
          carrier: result?.outbound_carrier ? result?.outbound_carrier : '-',
          tracking_number: result?.outbound_tracking_number
            ? result?.outbound_tracking_number
            : '-',
          address: result?.outbound_address ? (
            <div className="text-sm">
              {formatAddress(result?.outbound_address)}
            </div>
          ) : (
            '-'
          ),
          date: result?.timeStamp
            ? formatUnixTime(result?.timeStamp, 'dd/MM/yyyy')
            : '-',
          cost: result?.outbound_postage_cost
            ? getCurrencyFormat(
                result?.outbound_postage_cost?.amount,
                result?.outbound_postage_cost?.currency
              )
            : '-',
        });
      }
    });
    return formattedResultPostageType1;
  };

  const type1FormattedResultPostage: any[] = getType1FormattedPostage();
  const type1PostageTotalCost = type1FormattedResultPostage.reduce(
    (acc, curr) => {
      return (
        acc + (curr.cost ? parseFloat(curr.cost.replace(/[^\d.-]/g, '')) : 0)
      );
    },
    0
  );

  /* --------------------------------- Travel --------------------------------- */
  const travel = caseFacts?.travel_cost_data;
  const formattedResultTravel: {
    data: object;
    description: string;
    date: string;
    cost: string;
    vat: string;
    attachments: UserUploadedDocument[];
  }[] = [];
  travel?.forEach((result: CFCF_TravelCostLog, i) => {
    formattedResultTravel.push({
      data: { ...result, index: i },
      description: result?.description || '-',
      date: result?.date
        ? formatUnixTime(Number(result?.date), 'dd/MM/yyyy')
        : '-',
      cost: result?.amount
        ? getCurrencyFormat(result?.amount?.amount, result?.amount?.currency)
        : '-',
      vat: result?.VAT ? String(result?.VAT) : '-',
      attachments: result?.documents || [],
    });
  });

  const travelTotalCost = formattedResultTravel.reduce((acc, curr) => {
    const subTotal = curr.cost
      ? parseFloat(curr.cost.replace(/[^\d.-]/g, ''))
      : 0;
    return acc + subTotal + (subTotal * Number(curr.vat)) / 100;
  }, 0);

  /* ---------------------------- Other claim costs --------------------------- */
  const other = caseFacts?.other_cost_data || [];
  const formattedResultOther: {
    data: object;
    description: string;
    date: string;
    cost: string;
    vat: string;
    attachments: UserUploadedDocument[];
  }[] = [];
  other.forEach((result: CFCF_OtherCostLog, i) => {
    formattedResultOther.push({
      data: { ...result, index: i },
      description: result?.description || '-',
      date: result?.date
        ? formatUnixTime(Number(result?.date), 'dd/MM/yyyy')
        : '-',
      cost: result?.amount
        ? getCurrencyFormat(result?.amount?.amount, result?.amount?.currency)
        : '-',
      vat: result?.VAT ? String(result?.VAT) : '-',
      attachments: result?.documents || [],
    });
  });

  const otherTotalCost = formattedResultOther.reduce((acc, curr) => {
    const subTotal = curr.cost
      ? parseFloat(curr.cost.replace(/[^\d.-]/g, ''))
      : 0;
    return acc + subTotal + (subTotal * Number(curr.vat)) / 100;
  }, 0);

  const totalCostFromClaim = [
    administratitveTotalCost,
    repairTotalCost,
    partsUsedTotalCost,
    type1PartsUsedTotalCost,
    postageTotalCost,
    type1PostageTotalCost,
    travelTotalCost,
    otherTotalCost,
  ].reduce((a, b) => a + b, 0);

  const renderTableCost = (label: string, columns: any, data: any) => {
    return (
      <div>
        <CardWrapperWithHeader
          title={label}
          canCollapse={true}
          collapsedByDefault={true}
          smaller={true}
          noPadding={true}
        >
          <div className="max-w-[757px] rounded-lg border border-gray-100">
            <Table
              columns={columns}
              data={data}
              inner={true}
              enablePagination={data.length > 10}
              initialState={{
                showGlobalFilter: true,
              }}
              muiSearchTextFieldProps={{
                sx: {
                  display: 'none',
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  display: 'none',
                },
              }}
              muiTableHeadCellProps={{
                className: 'relative bg-gray-200 uppercase font-light',
              }}
              muiTableBodyCellProps={{
                className: 'px-4 py-0',
              }}
            />
          </div>
        </CardWrapperWithHeader>
      </div>
    );
  };

  const currencySymbol =
    LIST_CURRENCIES.find((i) => i.code === tenantInfo?.currency)
      ?.symbolNative || '';

  return (
    <CardWrapperWithHeader
      title={`${t('general.claimCosts')} - ${t(
        'general.total'
      )}: ${getCurrencyFormat(
        totalCostFromClaim || 0,
        tenantInfo?.currency || 'GBP'
      )}`}
      collapsedByDefault={!(totalCostFromClaim > 0)}
      headerRight={
        readOnly ? undefined : (
          <div className="flex gap-4">
            <ButtonIcon
              type="primary"
              label={t('general.logClaimCost')}
              Icon={WrenchScrewdriverIcon}
              iconLeft
              onClick={() => {
                setLogCostModal(true);
              }}
            />
          </div>
        )
      }
    >
      <div className="font-nunito flex flex-col gap-6">
        {administratitveTotalCost > 0 &&
          renderTableCost(
            `${t('general.administrativeTime')} - ${t(
              'general.total'
            )}: ${getCurrencyFormat(
              administratitveTotalCost || 0,
              tenantInfo?.currency || 'GBP'
            )}`,
            liteAdministrativeTimeColumns(
              setEditModal,
              currencySymbol,
              config.ACCOUNT.TAX_LABEL
            ),
            finalArrayForAdministrativeTimeTable
          )}
        {formattedResultRepairTime.length > 0 &&
          renderTableCost(
            `${t('general.repairTime')} - ${t(
              'general.total'
            )}: ${getCurrencyFormat(
              repairTotalCost || 0,
              tenantInfo?.currency || 'GBP'
            )}`,
            liteRepairTimeColumns(
              setEditModal,
              currencySymbol,
              config.ACCOUNT.TAX_LABEL
            ),
            formattedResultRepairTime
          )}
        {partsUsedTotalCost > 0 &&
          renderTableCost(
            `${t('general.partsUsed')} - ${t(
              'general.total'
            )}: ${getCurrencyFormat(
              partsUsedTotalCost || 0,
              tenantInfo?.currency || 'GBP'
            )}`,
            litePartsUsedColumns(setEditModal, config.ACCOUNT.TAX_LABEL),
            partsUserArrayParsed
          )}
        {type1PartsUsedTotalCost > 0 &&
          renderTableCost(
            `${t('general.partsUsed')} - ${t(
              'general.total'
            )}: ${getCurrencyFormat(
              type1PartsUsedTotalCost || 0,
              tenantInfo?.currency || 'GBP'
            )}`,
            type1PartsUsedColumns(
              // Use Type1-style modal actions (edit/delete) if provided from parent
              setModalData || (() => {})
            ),
            type1PartsUserArrayParsed
          )}
        {postageTotalCost > 0 &&
          renderTableCost(
            `${t('general.postage')} - ${t(
              'general.total'
            )}: ${getCurrencyFormat(
              postageTotalCost || 0,
              tenantInfo?.currency || 'GBP'
            )}`,
            litePostageColumns(
              setEditModal,
              setDocumentModal,
              config.ACCOUNT.TAX_LABEL
            ),
            formattedResultPostage
          )}
        {type1PostageTotalCost > 0 &&
          renderTableCost(
            `${t('general.postage')} - ${t(
              'general.total'
            )}: ${getCurrencyFormat(
              type1PostageTotalCost || 0,
              tenantInfo?.currency || 'GBP'
            )}`,
            type1PostageColumns,
            type1FormattedResultPostage
          )}

        {travelTotalCost > 0 &&
          renderTableCost(
            `${t('general.travel')} - ${t(
              'general.total'
            )}: ${getCurrencyFormat(
              travelTotalCost || 0,
              tenantInfo?.currency || 'GBP'
            )}`,
            liteTravelColumns(
              setEditModal,
              setDocumentModal,
              config.ACCOUNT.TAX_LABEL
            ),
            formattedResultTravel
          )}
        {otherTotalCost > 0 &&
          renderTableCost(
            `${t('general.other')} - ${t('general.total')}: ${getCurrencyFormat(
              otherTotalCost || 0,
              tenantInfo?.currency || 'GBP'
            )}`,
            liteOtherColumns(
              setEditModal,
              setDocumentModal,
              config.ACCOUNT.TAX_LABEL
            ),
            formattedResultOther
          )}
      </div>
    </CardWrapperWithHeader>
  );
}

export default ClaimCostsSectionDefault;
